import { logActivity } from '../utils/activityLog';
import { fmtDate } from '../utils/format';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Upload, RefreshCw, Filter, ChevronDown, ChevronUp,
  CheckCircle, XCircle, Clock, Trash2, Download,
  FileText, FileImage, File, Search, X, User, Eye, Scissors,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';
import { toast } from 'sonner';
import UploadPreviewModal from '../components/UploadPreviewModal';
import pdfOps from '../services/pdfOps';
import { getCurrentUser, hasPermission } from '../utils/roles';

const VERIFICATION_DOC_TYPES = [
  'Passport', 'Driver License', 'ID Front', 'ID Back',
  'Utility Bill', 'Bank Statement',
];
const UPLOAD_DOC_TYPES = [...VERIFICATION_DOC_TYPES, 'Contract', 'Other'];
const FILTER_DOC_TYPES = [...UPLOAD_DOC_TYPES, 'file', 'invoice', 'case_file'];
const DOC_TYPE_LABELS = {
  file: 'File',
  invoice: 'Invoice',
  case_file: 'Case File',
};
const STATUSES = ['pending', 'approved', 'declined'];
const getDocTypeLabel = (type) => DOC_TYPE_LABELS[type] || type || 'Document';
const DOC_SOURCE_TABS = [
  { key: 'pending', label: 'Pending', status: 'pending', countKey: 'pending' },
  { key: 'client', label: 'Client uploads', role: 'client', countKey: 'client' },
  { key: 'crm', label: 'CRM documents', role: 'agent', countKey: 'crm' },
];
const PENDING_SOURCE_TABS = [
  { key: 'client', label: 'Client uploads', role: 'client', countKey: 'pendingClient' },
  { key: 'crm', label: 'CRM uploads', role: 'agent', countKey: 'pendingCrm' },
];

// ── Small helpers ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status, label }) => {
  const cfg = {
    approved: { cls: 'bg-green-500/15 text-green-400 border border-green-500/30', Icon: CheckCircle },
    pending:  { cls: 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/30', Icon: Clock },
    declined: { cls: 'bg-red-500/15 text-red-400 border border-red-500/30', Icon: XCircle },
  };
  const { cls, Icon } = cfg[status] || { cls: 'bg-gray-500/15 text-gray-400', Icon: File };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      <Icon size={11} /> {label || status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const FileIcon = ({ name }) => {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (ext === 'pdf') return <FileText size={14} className="text-red-400 shrink-0" />;
  if (['jpg','jpeg','png','webp','gif'].includes(ext)) return <FileImage size={14} className="text-blue-400 shrink-0" />;
  return <File size={14} className="text-gray-400 shrink-0" />;
};

const getSourceLabel = (doc) => doc.uploaderRole === 'client' ? 'Client portal' : 'CRM upload';

// ── Client search with autocomplete ──────────────────────────────────────────
function ClientSearch({ value, onChange, cls, isDark, labels }) {
  const [query, setQuery]       = useState(value?.name || '');
  const [results, setResults]   = useState([]);
  const [open, setOpen]         = useState(false);
  const [loading, setLoading]   = useState(false);
  const containerRef            = useRef();
  const debounceRef             = useRef();

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (!containerRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback(async (q) => {
    if (!q.trim() || q.length < 2) { setResults([]); setOpen(false); return; }
    try {
      setLoading(true);
      const { data } = await api.get('/clients', { params: { search: q, limit: 8, page: 1 } });
      setResults(data.clients || []);
      setOpen(true);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  const handleInput = (e) => {
    const q = e.target.value;
    setQuery(q);
    if (!q) { onChange(null); setResults([]); setOpen(false); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(q), 280);
  };

  const select = (client) => {
    const label = `${client.firstName} ${client.lastName || ''}`.trim();
    setQuery(label);
    setOpen(false);
    onChange({ id: client.id, name: label, email: client.email });
  };

  const clear = () => { setQuery(''); onChange(null); setResults([]); };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
        <input
          value={query}
          onChange={handleInput}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={labels.searchClient}
          className={`${cls.input} pl-9 pr-8`}
        />
        {query && (
          <button onClick={clear} className="absolute right-2.5 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100">
            <X size={13} />
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <div className={`absolute z-50 w-full mt-1 rounded-lg shadow-xl border overflow-hidden ${isDark ? 'bg-[#1a1f3a] border-gray-700' : 'bg-white border-gray-200'}`}>
          {results.map(c => (
            <button
              key={c.id}
              onMouseDown={() => select(c)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${isDark ? 'hover:bg-[#252b4a]' : 'hover:bg-gray-50'}`}
            >
              <div className="w-7 h-7 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0">
                <User size={13} className="text-purple-400" />
              </div>
              <div className="min-w-0">
                <div className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {c.firstName} {c.lastName || ''}
                </div>
                <div className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{c.email}</div>
              </div>
              <span className={`ml-auto text-xs shrink-0 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>#{String(c.id).slice(0, 6)}</span>
            </button>
          ))}
        </div>
      )}

      {loading && (
        <div className={`absolute z-50 w-full mt-1 rounded-lg border px-3 py-2.5 text-xs ${isDark ? 'bg-[#1a1f3a] border-gray-700 text-gray-500' : 'bg-white border-gray-200 text-gray-400'}`}>
          {labels.searching}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Documents() {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const isDark = theme === 'dark';
  const docTypeLabel = (type) => t(`documents.docTypes.${type}`) || getDocTypeLabel(type);
  const statusLabel = (status) => t(`documents.statuses.${status}`) || status;
  const sourceLabel = (doc) => doc.uploaderRole === 'client' ? t('documents.clientPortal') : t('documents.crmUpload');
  const sourceTabLabel = (tab) => t(`documents.sourceTabs.${tab.key}`) || tab.label;
  const pendingSourceTabLabel = (tab) => t(`documents.pendingSourceTabs.${tab.key}`) || tab.label;
  const currentUser = getCurrentUser();
  const canUploadDocument = hasPermission(currentUser, 'Upload Document');
  const canApproveDocument = hasPermission(currentUser, 'Approve Document');
  const canDeleteDocument = hasPermission(currentUser, 'Delete Document');

  const [docs,       setDocs]       = useState([]);
  const [total,      setTotal]      = useState(0);
  const [tabCounts,  setTabCounts]  = useState({ pending: 0, pendingClient: 0, pendingCrm: 0, client: 0, crm: 0 });
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading,    setLoading]    = useState(true);

  // Filters
  const [search,       setSearch]       = useState('');
  const [filterType,   setFilterType]   = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showFilters,  setShowFilters]  = useState(false);
  const [sourceTab,    setSourceTab]    = useState('pending');
  const [pendingSourceTab, setPendingSourceTab] = useState('client');

  // Upload modal
  const [showUpload,  setShowUpload]  = useState(false);
  const [selClient,   setSelClient]   = useState(null);   // { id, name, email }
  const [docType,     setDocType]     = useState(UPLOAD_DOC_TYPES[0]);
  const [uploadFile,  setUploadFile]  = useState(null);
  const [uploading,   setUploading]   = useState(false);
  const [dragOver,    setDragOver]    = useState(false);
  const fileRef = useRef();

  // PDF editor state
  const [editorDoc,   setEditorDoc]   = useState(null);

  const LIMIT = 25;

  const cls = {
    card:       `rounded-lg border ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-white border-gray-200'}`,
    input:      `w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white placeholder-gray-600' : 'bg-white border-gray-300 text-gray-900'}`,
    select:     `w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`,
    label:      `block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`,
    th:         `px-3 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`,
    td:         `px-3 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`,
    btn:        `px-3 py-1.5 text-sm border rounded-md transition-colors ${isDark ? 'border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`,
    btnPrimary: 'px-4 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-60',
  };

  useEffect(() => { logActivity('view', 'documents', null, 'Viewed Documents list'); }, []);
  useEffect(() => { load(); }, [page, filterType, filterStatus, sourceTab, pendingSourceTab]); // eslint-disable-line
  useEffect(() => { loadSummary(); }, []); // eslint-disable-line

  const load = async () => {
    try {
      setLoading(true);
      const params = { page, limit: LIMIT };
      const activeSource = DOC_SOURCE_TABS.find(t => t.key === sourceTab);
      const activePendingSource = PENDING_SOURCE_TABS.find(t => t.key === pendingSourceTab);
      if (sourceTab === 'pending') params.uploader_role = activePendingSource?.role;
      else if (activeSource?.role) params.uploader_role = activeSource.role;
      if (activeSource?.status) params.status = activeSource.status;
      if (filterType)   params.doc_type = filterType;
      if (!activeSource?.status && filterStatus) params.status = filterStatus;
      if (search)       params.search   = search;
      const { data } = await api.get('/documents', { params });
      setDocs(data.documents || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch { toast.error('Failed to load documents'); }
    finally { setLoading(false); }
  };

  const loadSummary = async () => {
    try {
      const { data } = await api.get('/documents/summary');
      setTabCounts({
        pending: data.pending || 0,
        pendingClient: data.pendingClient || 0,
        pendingCrm: data.pendingCrm || 0,
        client: data.client || 0,
        crm: data.crm || 0,
      });
    } catch {}
  };

  const changeStatus = async (docId, newStatus) => {
    if (!canApproveDocument) return;
    try {
      await api.patch(`/documents/${docId}/status`, { status: newStatus });
      toast.success(newStatus === 'declined' ? 'Document declined and deleted' : `Status -> ${newStatus}`);
      loadSummary();
      load();
    } catch { toast.error('Failed to update status'); }
  };

  const deleteDoc = async (doc) => {
    if (!canDeleteDocument) return;
    if (!window.confirm(`Delete "${doc.fileName}"?`)) return;
    try { await api.delete(`/documents/${doc.id}`); toast.success('Deleted'); loadSummary(); load(); }
    catch { toast.error('Delete failed'); }
  };

  const downloadDoc = async (doc) => {
    // open in new tab — the backend sends inline content-disposition for images/pdf
    const a = document.createElement('a');
    a.target = '_blank';
    try {
      const { data } = await api.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(data);
      a.href = blobUrl;
      a.download = doc.fileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch {
      toast.error('Download failed');
    }
  };

  const resetUpload = () => {
    setSelClient(null); setDocType(UPLOAD_DOC_TYPES[0]); setUploadFile(null); setDragOver(false);
  };
  const closeUpload = () => { setShowUpload(false); resetUpload(); };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) setUploadFile(f);
  };

  const submitUpload = async () => {
    if (!canUploadDocument) return;
    if (!selClient) { toast.error('Select a client first'); return; }
    if (!uploadFile) { toast.error('Select a file'); return; }
    const fd = new FormData();
    fd.append('file', uploadFile);
    try {
      setUploading(true);
      await api.post('/documents/upload', fd, {
        params: { client_id: selClient.id, doc_type: docType },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Document uploaded for ${selClient.name}`);
      closeUpload();
      loadSummary();
      load();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const filterCount = [filterType, filterStatus, search].filter(Boolean).length;
  const showCrmActions = sourceTab === 'crm' && canUploadDocument;

  const isPdf = (doc) => (doc.fileName || '').toLowerCase().endsWith('.pdf');

  const splitPdf = async (doc) => {
    const input = window.prompt(
      `Split "${doc.fileName}" — enter ranges (e.g. "1-3,4-7"):`, '1-1');
    if (!input) return;
    const ranges = input.split(',').map(s => {
      const [a, b] = s.split('-').map(n => parseInt(n.trim(), 10));
      return { start: a, end: b || a };
    }).filter(r => r.start && r.end);
    if (!ranges.length) return;
    try {
      const r = await pdfOps.split(doc.id, ranges);
      toast.success(`Split into ${r.newDocumentIds.length} new docs`);
      loadSummary();
      load();
    } catch { toast.error('Split failed'); }
  };

  return (
    <div className="p-6 space-y-4">

      {/* ── Toolbar ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {showCrmActions && (
            <button
              onClick={() => setShowUpload(true)}
              className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
            >
              <Upload size={14} /> {t('documents.uploadDocument')}
            </button>
          )}

          <button
            onClick={() => setShowFilters(v => !v)}
            className={`flex items-center gap-1.5 ${cls.btn} ${filterCount > 0 ? 'border-purple-500 text-purple-400' : ''}`}
          >
            <Filter size={14} />
            {t('documents.filters')}
            {filterCount > 0 && (
              <span className="bg-purple-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">{filterCount}</span>
            )}
            {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>

        <div className="flex items-center gap-2">
          <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{total} {t('documents.documents')}</span>
          <button onClick={load} className={cls.btn}><RefreshCw size={14} /></button>
        </div>
      </div>

      {/* ── Filter panel ────────────────────────────────────────────── */}
      <div className={`inline-flex p-1 rounded-lg border ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-white border-gray-200'}`}>
        {DOC_SOURCE_TABS.map(tab => {
          const active = sourceTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => {
                setSourceTab(tab.key);
                setFilterStatus('');
                setPage(1);
              }}
              className={`inline-flex items-center px-4 py-1.5 rounded-md text-sm transition-colors ${
                active
                  ? 'bg-purple-600 text-white'
                  : isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-white/5' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <span>{sourceTabLabel(tab)}</span>
              <span className={`ml-2 inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${
                active
                  ? 'bg-white/20 text-white'
                  : isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'
              }`}>
                {tabCounts[tab.countKey] || 0}
              </span>
            </button>
          );
        })}
      </div>

      {showFilters && (
        <div className={`${cls.card} p-4`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Search */}
            <div className="col-span-2">
              <label className={cls.label}>{t('documents.searchLabel')}</label>
              <div className="relative">
                <Search size={13} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (setPage(1), load())}
                  placeholder={t('documents.searchPlaceholder')}
                  className={`${cls.input} pl-9`}
                />
              </div>
            </div>

            {/* Doc type */}
            <div>
              <label className={cls.label}>{t('documents.documentType')}</label>
              <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }} className={cls.select}>
                <option value="">{t('documents.allTypes')}</option>
                {FILTER_DOC_TYPES.map(type => <option key={type} value={type}>{docTypeLabel(type)}</option>)}
              </select>
            </div>

            {/* Status */}
            <div>
              <label className={cls.label}>{t('documents.status')}</label>
              <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }} className={cls.select}>
                <option value="">{t('documents.allStatuses')}</option>
                {STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
              </select>
            </div>
          </div>

          <div className="flex gap-2 mt-3">
            <button onClick={() => { setPage(1); load(); }} className={cls.btnPrimary}>{t('documents.apply')}</button>
            <button onClick={() => { setSearch(''); setFilterType(''); setFilterStatus(''); setPage(1); setTimeout(load, 0); }} className={cls.btn}>{t('documents.clear')}</button>
          </div>
        </div>
      )}

      {/* ── Table ───────────────────────────────────────────────────── */}
      <div className={`${cls.card} overflow-hidden`}>
        {sourceTab === 'pending' && (
          <div className={`px-3 py-2 border-b ${isDark ? 'border-gray-800 bg-[#0a0e1a]' : 'border-gray-200 bg-gray-50'}`}>
            <div className={`inline-flex p-1 rounded-md border ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-white border-gray-200'}`}>
              {PENDING_SOURCE_TABS.map(tab => {
                const active = pendingSourceTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => {
                      setPendingSourceTab(tab.key);
                      setPage(1);
                    }}
                    className={`inline-flex items-center px-3 py-1.5 rounded text-xs transition-colors ${
                      active
                        ? 'bg-purple-500/20 text-purple-300'
                        : isDark ? 'text-gray-500 hover:text-gray-300 hover:bg-white/5' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    <span>{pendingSourceTabLabel(tab)}</span>
                    <span className={`ml-2 inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${
                      active
                        ? 'bg-purple-500/25 text-purple-200'
                        : isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {tabCounts[tab.countKey] || 0}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`border-b ${isDark ? 'bg-[#0a0e1a] border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
              <tr>
                {[
                  '#',
                  t('documents.client'),
                  t('documents.documentType'),
                  t('documents.file'),
                  sourceTab === 'crm' ? t('documents.visibility') : t('documents.reviewStatus'),
                  t('documents.uploadedBy'),
                  t('documents.date'),
                  t('documents.actions'),
                ].map(h => (
                  <th key={h} className={cls.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
              {loading ? (
                <tr><td colSpan={8} className={`${cls.td} text-center py-14`}>{t('documents.loading')}</td></tr>
              ) : docs.length === 0 ? (
                <tr>
                  <td colSpan={8} className={`${cls.td} text-center py-14`}>
                    <Upload size={32} className={`mx-auto mb-3 ${isDark ? 'text-gray-700' : 'text-gray-300'}`} />
                    <p className={isDark ? 'text-gray-500' : 'text-gray-400'}>{t('documents.empty')}</p>
                    <p className={`text-xs mt-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                      {sourceTab === 'pending'
                        ? (pendingSourceTab === 'client' ? t('documents.emptyPendingClient') : t('documents.emptyPendingCrm'))
                        : sourceTab === 'client' ? t('documents.emptyClientUploads') : t('documents.emptyCrmUploads')}
                    </p>
                  </td>
                </tr>
              ) : docs.map((doc, i) => (
                <tr key={doc.id} className={isDark ? 'hover:bg-[#1a1f3a]' : 'hover:bg-gray-50'}>
                  <td className={`${cls.td} text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{(page-1)*LIMIT+i+1}</td>

                  {/* Client */}
                  <td className={cls.td}>
                    <div className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{doc.clientName}</div>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{doc.clientEmail}</div>
                  </td>

                  {/* Doc type */}
                  <td className={cls.td}>
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${isDark ? 'bg-[#1a1f3a] text-gray-300 border border-gray-700' : 'bg-gray-100 text-gray-700'}`}>
                      {docTypeLabel(doc.docType)}
                    </span>
                  </td>

                  {/* File name */}
                  <td className={cls.td}>
                    <div className="flex items-center gap-1.5 max-w-[180px]">
                      <FileIcon name={doc.fileName} />
                      <span className="truncate text-xs font-mono" title={doc.fileName}>{doc.fileName}</span>
                    </div>
                  </td>

                  {/* Status */}
                  <td className={cls.td}>
                    {sourceTab === 'crm' ? (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${
                        doc.showToClient
                          ? 'bg-green-500/15 text-green-400 border-green-500/30'
                          : isDark ? 'bg-gray-500/10 text-gray-400 border-gray-700' : 'bg-gray-100 text-gray-600 border-gray-200'
                      }`}>
                        {doc.showToClient ? t('documents.visibleToClient') : t('documents.draft')}
                      </span>
                    ) : (
                      <div className="flex flex-col items-start gap-1">
                        <StatusBadge status={doc.status} label={statusLabel(doc.status)} />
                        {doc.uploaderRole !== 'client' && (
                          <span className={`text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            {doc.showToClient ? t('documents.visibleToClient') : t('documents.crmDraft')}
                          </span>
                        )}
                      </div>
                    )}
                  </td>

                  {/* Uploaded by */}
                  <td className={`${cls.td} text-xs`}>
                    <div className={isDark ? 'text-gray-300' : 'text-gray-700'}>{doc.uploadedBy || '—'}</div>
                    <div className={`mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      <span>{sourceLabel(doc)}</span>
                      {doc.showToClient ? <span className="text-green-400">{t('documents.visibleToClient')}</span> : doc.uploaderRole !== 'client' ? <span>{t('documents.draft')}</span> : null}
                    </div>
                  </td>

                  {/* Date */}
                  <td className={`${cls.td} text-xs whitespace-nowrap`}>{fmtDate(doc.createdAt)}</td>

                  {/* Actions */}
                  <td className={cls.td}>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setEditorDoc(doc)}
                        className={`p-1.5 rounded transition-colors ${isDark ? 'text-gray-400 hover:text-purple-400 hover:bg-purple-500/10' : 'text-gray-500 hover:text-purple-600 hover:bg-purple-50'}`}
                        title={t('documents.previewDocument')}
                      >
                        <Eye size={13} />
                      </button>
                      {/* Split (PDF only) */}
                      {isPdf(doc) && (
                        <button
                          onClick={() => splitPdf(doc)}
                          className={`p-1.5 rounded transition-colors ${isDark ? 'text-gray-400 hover:text-orange-400 hover:bg-orange-500/10' : 'text-gray-500 hover:text-orange-600 hover:bg-orange-50'}`}
                          title={t('documents.splitPdf')}
                        >
                          <Scissors size={13} />
                        </button>
                      )}
                      {/* Download */}
                      <button
                        onClick={() => downloadDoc(doc)}
                        className={`p-1.5 rounded transition-colors ${isDark ? 'text-gray-400 hover:text-blue-400 hover:bg-blue-500/10' : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'}`}
                        title={t('documents.download')}
                      >
                        <Download size={13} />
                      </button>

                      {/* Approve */}
                      {canApproveDocument && doc.status !== 'approved' && (
                        <button onClick={() => changeStatus(doc.id, 'approved')}
                          className="p-1.5 rounded text-green-400 hover:bg-green-500/10 transition-colors" title={t('documents.approve')}>
                          <CheckCircle size={13} />
                        </button>
                      )}

                      {/* Decline */}
                      {canApproveDocument && doc.uploaderRole === 'client' && doc.status !== 'declined' && (
                        <button onClick={() => changeStatus(doc.id, 'declined')}
                          className="p-1.5 rounded text-red-400 hover:bg-red-500/10 transition-colors" title={t('documents.declineAndDelete')}>
                          <XCircle size={13} />
                        </button>
                      )}

                      {/* Reset to pending */}
                      {canApproveDocument && doc.uploaderRole === 'client' && doc.status !== 'pending' && (
                        <button onClick={() => changeStatus(doc.id, 'pending')}
                          className={`p-1.5 rounded transition-colors ${isDark ? 'text-gray-500 hover:text-gray-300 hover:bg-gray-700' : 'text-gray-400 hover:bg-gray-100'}`} title={t('documents.resetToPending')}>
                          <Clock size={13} />
                        </button>
                      )}

                      {/* Delete */}
                      {canDeleteDocument && (
                        <button onClick={() => deleteDoc(doc)}
                          className="p-1.5 rounded text-red-400 hover:bg-red-500/10 transition-colors" title={t('documents.delete')}>
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={`flex items-center justify-between px-4 py-3 border-t ${isDark ? 'border-gray-800 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
            <span className="text-xs">
              {Math.min((page-1)*LIMIT+1, total)}-{Math.min(page*LIMIT, total)} {t('documents.of')} {total}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1} className={`${cls.btn} disabled:opacity-40`}>‹</button>
              <span className={`px-3 py-1 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page===totalPages} className={`${cls.btn} disabled:opacity-40`}>›</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Upload Preview (Удалить / Шаблон / Опубликовать) ─────────── */}
      {editorDoc && (
        <UploadPreviewModal
          doc={editorDoc}
          isDark={isDark}
          canDelete={canDeleteDocument}
          canApprove={canApproveDocument}
          canManageCrmDocument={canUploadDocument}
          onClose={() => setEditorDoc(null)}
          onDeleted={() => { loadSummary(); load(); }}
          onTemplated={() => { loadSummary(); load(); }}
          onPublished={() => { loadSummary(); load(); }}
          onStatusChanged={() => { loadSummary(); load(); }}
        />
      )}

      {/* ── Upload Modal ────────────────────────────────────────────── */}
      {showUpload && canUploadDocument && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-lg rounded-xl shadow-2xl ${isDark ? 'bg-[#1a1f3a]' : 'bg-white'}`}>

            {/* Header */}
            <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div>
                <h2 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('documents.uploadDocument')}</h2>
                <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {t('documents.uploadHint')}
                </p>
              </div>
              <button onClick={closeUpload} className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'} text-xl leading-none`}>✕</button>
            </div>

            <div className="px-6 py-5 space-y-4">

              {/* Client search */}
              <div>
                <label className={cls.label}>{t('documents.client')} *</label>
                <ClientSearch value={selClient} onChange={setSelClient} cls={cls} isDark={isDark} labels={{ searchClient: t('documents.searchClient'), searching: t('documents.searching') }} />
                {selClient && (
                  <div className={`mt-1.5 flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs ${isDark ? 'bg-purple-500/10 text-purple-300 border border-purple-500/20' : 'bg-purple-50 text-purple-700 border border-purple-200'}`}>
                    <User size={11} />
                    <span className="font-medium">{selClient.name}</span>
                    <span className="opacity-60">{selClient.email}</span>
                  </div>
                )}
              </div>

              {/* Document type */}
              <div>
                <label className={cls.label}>{t('documents.documentType')} *</label>
                <select value={docType} onChange={e => setDocType(e.target.value)} className={cls.select}>
                  {UPLOAD_DOC_TYPES.map(type => <option key={type} value={type}>{docTypeLabel(type)}</option>)}
                </select>
              </div>

              {/* File drop zone */}
              <div>
                <label className={cls.label}>{t('documents.file')} * &nbsp;<span className={isDark ? 'text-gray-600' : 'text-gray-400'}>{t('documents.fileHint')}</span></label>
                <div
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                    dragOver
                      ? 'border-purple-500 bg-purple-500/10'
                      : uploadFile
                        ? (isDark ? 'border-green-500/50 bg-green-500/5' : 'border-green-400 bg-green-50')
                        : (isDark ? 'border-gray-700 hover:border-purple-600 hover:bg-purple-500/5' : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50/50')
                  }`}
                >
                  {uploadFile ? (
                    <div>
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <FileIcon name={uploadFile.name} />
                        <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{uploadFile.name}</span>
                      </div>
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{(uploadFile.size / 1024).toFixed(1)} KB - {t('documents.clickToReplace')}</p>
                    </div>
                  ) : (
                    <div>
                      <Upload size={22} className={`mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t('documents.dropFile')}</p>
                    </div>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  className="hidden"
                  onChange={e => setUploadFile(e.target.files[0] || null)}
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={closeUpload} className={cls.btn}>{t('documents.cancel')}</button>
                <button
                  onClick={submitUpload}
                  disabled={uploading || !selClient || !uploadFile}
                  className={cls.btnPrimary}
                >
                  {uploading ? t('documents.uploading') : t('documents.upload')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
